import { useState, useMemo, useCallback } from 'react';
import { Stack, Group, Text, Button, ActionIcon, Badge, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconGripVertical, IconX, IconPlus } from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVideos } from '@/features/media/useVideos';
import { VideoPickerModal } from './VideoPickerModal';
import type { PlaylistMediaPosition } from '@/lib/types/Playlist';
import type { MediaType } from '@/lib/types/Media';

interface PlaylistItemsManagerProps {
  mediaPositions: PlaylistMediaPosition[];
  onChange: (positions: PlaylistMediaPosition[]) => void;
  mediaType: MediaType;
}

interface VideoInfo {
  title: string;
  isActive: boolean;
}

export function PlaylistItemsManager({ mediaPositions, onChange, mediaType }: PlaylistItemsManagerProps) {
  const { videos } = useVideos();
  const [activeId, setActiveId] = useState<number | null>(null);
  const [pickerOpened, { open: openPicker, close: closePicker }] = useDisclosure(false);

  // Build lookup map for video titles from cached data
  const videoMap = useMemo(() => {
    const map = new Map<number, VideoInfo>();
    for (const v of videos) {
      map.set(v.media.mediaId, {
        title: v.media.title,
        isActive: v.media.mediaStateType === 'active',
      });
    }
    return map;
  }, [videos]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // IDs for SortableContext (must be strings or numbers)
  const sortableIds = useMemo(
    () => mediaPositions.map(p => p.mediaId),
    [mediaPositions]
  );

  const excludeMediaIds = useMemo(
    () => mediaPositions.map(p => p.mediaId),
    [mediaPositions]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = mediaPositions.findIndex(p => p.mediaId === active.id);
    const newIndex = mediaPositions.findIndex(p => p.mediaId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(mediaPositions, oldIndex, newIndex);
    // Recalculate positions (0-based sequential)
    const updated = reordered.map((p, i) => ({ ...p, position: i }));
    onChange(updated);
  }, [mediaPositions, onChange]);

  const handleRemove = useCallback((mediaId: number) => {
    const filtered = mediaPositions
      .filter(p => p.mediaId !== mediaId)
      .map((p, i) => ({ ...p, position: i }));
    onChange(filtered);
  }, [mediaPositions, onChange]);

  const handleAddVideos = useCallback((mediaIds: number[]) => {
    const startPos = mediaPositions.length;
    const newPositions: PlaylistMediaPosition[] = mediaIds.map((mediaId, i) => ({
      mediaId,
      position: startPos + i,
    }));
    onChange([...mediaPositions, ...newPositions]);
  }, [mediaPositions, onChange]);

  function getVideoTitle(mediaId: number): string {
    return videoMap.get(mediaId)?.title ?? `Unknown video (ID: ${mediaId})`;
  }

  const activeItem = activeId != null
    ? mediaPositions.find(p => p.mediaId === activeId)
    : null;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={600} size="md">Playlist Items</Text>
          <Badge size="sm" variant="light">{mediaPositions.length}</Badge>
        </Group>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={openPicker}
        >
          Add Videos
        </Button>
      </Group>

      {mediaPositions.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No items in this playlist. Add videos to get started.
        </Text>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <Stack gap={0}>
              {mediaPositions.map((pos, index) => (
                <SortableItem
                  key={pos.mediaId}
                  mediaId={pos.mediaId}
                  position={index + 1}
                  title={getVideoTitle(pos.mediaId)}
                  onRemove={() => handleRemove(pos.mediaId)}
                />
              ))}
            </Stack>
          </SortableContext>
          <DragOverlay>
            {activeItem && (
              <ItemDisplay
                position={mediaPositions.findIndex(p => p.mediaId === activeItem.mediaId) + 1}
                title={getVideoTitle(activeItem.mediaId)}
                isDragging
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      <VideoPickerModal
        opened={pickerOpened}
        onClose={closePicker}
        onAdd={handleAddVideos}
        excludeMediaIds={excludeMediaIds}
        mediaType={mediaType}
      />
    </Stack>
  );
}

/**
 * Sortable item wrapper using @dnd-kit/sortable.
 * Uses setActivatorNodeRef so only the grip handle initiates drag.
 */
function SortableItem({
  mediaId,
  position,
  title,
  onRemove,
}: {
  mediaId: number;
  position: number;
  title: string;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mediaId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Group
        gap="sm"
        wrap="nowrap"
        px="sm"
        py="xs"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
      >
        <ActionIcon
          ref={setActivatorNodeRef}
          {...listeners}
          variant="subtle"
          color="gray"
          style={{ cursor: 'grab', touchAction: 'none' }}
          aria-label="Drag to reorder"
        >
          <IconGripVertical size={16} />
        </ActionIcon>
        <Text size="xs" c="dimmed" w={30} ta="right">#{position}</Text>
        <Text size="sm" style={{ flex: 1 }} truncate="end">{title}</Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onRemove}
          aria-label="Remove item"
        >
          <IconX size={14} />
        </ActionIcon>
      </Group>
    </div>
  );
}

/** Static display for DragOverlay (renders styled copy at cursor) */
function ItemDisplay({
  position,
  title,
  isDragging,
}: {
  position: number;
  title: string;
  isDragging?: boolean;
}) {
  return (
    <Box
      px="sm"
      py="xs"
      bg="white"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-sm)',
        boxShadow: isDragging ? 'var(--mantine-shadow-md)' : undefined,
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <IconGripVertical size={16} color="var(--mantine-color-gray-5)" />
        <Text size="xs" c="dimmed" w={30} ta="right">#{position}</Text>
        <Text size="sm" truncate="end">{title}</Text>
      </Group>
    </Box>
  );
}
